/**
 * Screens / window-screens SAP endpoints. Mirrors al_screens/src/utils/API.js.
 *
 * Several response rows are OData entities (single, not arrays) — those are
 * unwrapped via the `d` envelope. Where SAP returns arrays, the OData
 * `d.results` envelope is unwrapped at the boundary.
 *
 * Field-rename impact at a glance (from API_SPEC.md §5):
 *   - `screen/items` ItemToItemConfig.results[].Name strings drive the configurator
 *   - `screen/options` nested keys ScreenToGrill / ScreenToMesh / ScreenToPetFlap
 *   - `Material` ↔ `FrontEndMat` join key for promo discount
 *   - `SalesOrg` formatting (leading zeros matter)
 */

import { apiGet, apiPost } from './client'
import { resolveApiBase } from './config'
import type { ODataEntity, ODataResults } from './types'

// ---------- Common enums / characteristic names ----------

export type ProdFam =
  | 'DS' // Door Sliding
  | 'WS' // Window Sliding
  | 'WA' // Window Awning
  | 'SCD' // Screen Door
  | 'WBF' // Bi-fold Window
  | (string & {})

export type ProdType = 'DR' | 'WD' | 'BR' | 'RET' | 'SS' | (string & {})

export type BalRating = 'LOW' | 'BAL12_5' | 'BAL19' | 'BAL29' | 'BAL40' | 'BALFZ' | (string & {})

/** Names the configurator reads via Helper.js#getConfigValue. */
export const CONFIG_NAME = {
  PROD_TYPE: 'PROD_TYPE',
  PROD_FAM: 'PROD_FAM',
  FRAME_HT: 'FRAME_HT',
  FRAME_WID: 'FRAME_WID',
  TEMPLATE: 'TEMPLATE',
  DR_SUI: 'DR_SUI',
  BAL_RATING: 'BAL_RATING',
  FM_ALU_COLOUR: 'FM_ALU_COLOUR',
  FAMILY: 'FAMILY',
  PROD_PLAT: 'PROD_PLAT',
} as const

// ---------- screen/items ----------

export type ItemConfig = {
  Name: string
  Characteristic: string
  Value: string
  Desc: string
  ItemNo: string
  SONo: string
}

export type ScreenItem = {
  SONo: string
  ItemNo: string
  ProdType: ProdType
  /**
   * The opening's tag from the original order ("W01") — NOT a product name
   * (the product name is the PROD_FAM characteristic's Value). Field name
   * verified against the live test tenant; API_SPEC.md's "ProdDescr"
   * spelling was a test-fixture artefact and does not exist in real
   * responses.
   */
  ProdDesc: string
  SalesOrg: string
  Quantity: number
  DimReplaceText: string
  Image: string
  IsCharactImage: boolean
  FixedWindow: boolean
  ScreenApply: boolean
  Material: string
  Message: string
  ItemToItemConfig: { results: ItemConfig[] }
}

export async function getScreenItems(SONo: string): Promise<ScreenItem[]> {
  const res = await apiGet<ODataResults<ScreenItem>>('/v1/screen/items', { SONo })
  return res.d.results
}

// ---------- screen/options ----------

export type ScreenSubOption = {
  ProdType: ProdType
  Desc: string
  ProdFam: ProdFam
  Price: string
  NonTradeCharge: string
  Image: string
  Sortorder: number
  InfoText?: string
}

export type ScreenOption = {
  ProdType: ProdType
  Desc: string
  ProdFam: ProdFam
  ItemNo?: string
  Quantity?: number
  Price: string
  FitCost: string
  TripleLockCost: string
  NonTradeCharge: string
  Sortorder: number
  IsGrillType: boolean
  IsMeshType: boolean
  IsPetFlap: boolean
  Image: string
  IsCharactImage?: boolean
  InfoText?: string
  ScreenToGrill: { results: ScreenSubOption[] }
  ScreenToMesh: { results: ScreenSubOption[] }
  ScreenToPetFlap: { results: ScreenSubOption[] }
}

export async function getScreenOptions(params: {
  prodFam: ProdFam
  balRating: BalRating
  salesOrg: string
  soNo: string
  itemNo: string
}): Promise<ScreenOption[]> {
  const res = await apiGet<ODataResults<ScreenOption>>('/v1/screen/options', {
    salesOrg: params.salesOrg,
    balRating: params.balRating,
    prodFam: params.prodFam,
    soNo: params.soNo,
    itemNo: params.itemNo,
  })
  return res.d.results
}

// ---------- screen/colours ----------

export type ScreenColour = {
  Desc: string
  ColourCode: string
  Sortorder: number
  /** Empty string means "valid for any sales org". */
  SalesOrg: string
}

export async function getScreenColours(salesOrg: string): Promise<ScreenColour[]> {
  const res = await apiGet<ODataResults<ScreenColour>>('/v1/screen/colours', { salesOrg })
  return res.d.results
}

// ---------- screen/getDiscount ----------

export type DiscountRow = {
  /** SAP material number — matches ScreenItem.Material as the join key. */
  FrontEndMat: string
  /** Percentage as a string. parseFloat(d) / 100 at use sites. */
  Discount: string
}

export type DiscountResponse = {
  d: {
    ToItem: { results: DiscountRow[] }
  }
}

export async function getScreenDiscount(promoCode: string, salesOrg: string): Promise<DiscountRow[]> {
  const res = await apiGet<DiscountResponse>('/v1/screen/getDiscount', {
    promoCode,
    salesOrg,
  })
  return res.d.ToItem.results
}

// ---------- screen/installCost ----------

export type InstallCost = {
  Amount: string
}

export async function getScreenInstallCost(SONo: string): Promise<number> {
  const res = await apiGet<ODataEntity<InstallCost>>('/v1/screen/installCost', { SONo })
  return parseFloat(res.d.Amount)
}

// ---------- screen/saveOrder ----------

/**
 * Per-line action sent to SAP in BOTH saveOrder payloads. These are the
 * legacy item STATUS strings (AppConstants.js), passed through verbatim —
 * NOT the 'ADD'/'EDIT'/'DELETE' the API_SPEC example inferred; the live
 * frontend has only ever sent these.
 */
export type ScreenLineAction = 'Selected' | 'Cancelled' | 'Not Selected' | 'Not Applicable'

export type HanaScreenLineItem = {
  MaterialNo: string
  Qty: number
  Price: string
  SONO: string
  SOItemNo: string
  FrameColor: string
  ProdType: string
  ProdFam: string
  ProdDesc: string
  Height: string
  Width: string
  Screen: string
  ScreenDesc: string
  Action: ScreenLineAction
  IsCharactImage: 0 | 1
  Image: string
  HasTripleLock: 0 | 1
  FitCost: number
  TripleLockCost: number
  NonTradeCharge: number
  ToGrill: Array<{ Price: number; ProdType: string; ProdFam: string; ProdDesc: string; NonTradeCharge: number }>
  ToMesh: Array<{ Price: number; ProdType: string; ProdFam: string; ProdDesc: string; NonTradeCharge: number }>
  ToPetflap: Array<{ Price: number; ProdType: string; ProdFam: string; ProdDesc: string; NonTradeCharge: number }>
  RoomName: string
}

export type HanaScreenPayload = {
  SalesOrg: string
  FName: string
  LName: string
  Comments: string
  BalRating: BalRating
  Status: 'Pending' | string
  TelNumber: string
  Street: string
  PostCode: string
  City: string
  State: string
  TotalPrice: string
  PaymentStatus: string
  ShipmentType: string
  ShipmentValue: string
  Email: string
  DefaultFrameColor: string
  AddrNo: string
  PaymentURL: string
  InstallCost: number
  PromoCode: string
  PromoAmount: number
  ToScrItm: HanaScreenLineItem[]
}

export type IdocScreenItem = {
  Action: ScreenLineAction
  SONo: string
  ItemNo: string
  BalRating: BalRating
  ProdFam: string
  ProdType: string
  SalesOrg: string
  ScreenToMesh: Array<{ ProdType: string; SONo: string; ItemNo: string }>
  ScreenToGrill: Array<{ ProdType: string; SONo: string; ItemNo: string }>
  ScreenToPetFlap: Array<{ ProdType: string; SONo: string; ItemNo: string }>
  HasTripleLock: boolean
  ColourCode: string
}

export type IdocScreenPayload = {
  RefNo: string
  FirstName: string
  LastName: string
  Email: string
  TelNumber: string
  Street: string
  City: string
  State: string
  PostCode: string
  Country: 'AU' | string
  PromoCode: string
  QuotationToScreen: IdocScreenItem[]
}

export type ScreenSaveOrderPayload = {
  HanaPayload: HanaScreenPayload
  IdocPayload: IdocScreenPayload
}

export type ScreenSaveOrderResponse = {
  RefNo: string
}

export async function postScreenSaveOrder(
  payload: ScreenSaveOrderPayload,
): Promise<ScreenSaveOrderResponse> {
  return apiPost<ScreenSaveOrderResponse>('/v1/screen/saveOrder', payload)
}

// ---------- screen/generatePayment ----------

export type GeneratePaymentResponse = {
  paymentURL: string
}

export async function getScreenPaymentUrl(refNo: string, surname: string): Promise<string> {
  const res = await apiGet<GeneratePaymentResponse>('/v1/screen/generatePayment', {
    refNo,
    surname,
  })
  return res.paymentURL
}

// ---------- hana/addressLookup + hana/salesOrder ----------

export type HanaAddressRow = {
  AddrNo: string
  PostCode?: string
  // Fields the live SAP addressLookup returns but the app historically
  // ignored. Surfaced so a multi-lot address can be labelled for the customer
  // (see ScreenHome's lot-select step). All optional/tolerant — any may be
  // absent, and `LotNo` can come through as junk ("LOT", "") on some records.
  LotNo?: string
  Name1?: string
  Name2?: string
  Name3?: string
  HNo?: string
  Unit?: string
  Suburb?: string
  Street?: string
  Region?: string
  Country?: string
}

export async function hanaAddressLookup(params: {
  unit?: string
  street: string
  postcode: string
  lot?: string
  houseNo?: string
  suburb: string
  region: string
}): Promise<HanaAddressRow[]> {
  const res = await apiGet<ODataResults<HanaAddressRow>>('/v1/hana/addressLookup', {
    street: params.street,
    postcode: params.postcode,
    lot: params.lot ?? '',
    houseNo: params.houseNo ?? '',
    suburb: params.suburb,
    region: params.region,
    unit: params.unit ?? '',
  })
  return res.d.results
}

export type HanaSalesOrderRow = {
  AddrNo: string
  SONo: string
  /** Partner function on the order (e.g. "WE"). Present on live responses. */
  Partner?: string
}

export async function getHanaSalesOrders(addressNo: string): Promise<HanaSalesOrderRow[]> {
  const res = await apiGet<ODataResults<HanaSalesOrderRow>>('/v1/hana/salesOrder', { addressNo })
  return res.d.results
}

// ---------- Images ----------

export function getScreenImageSrc(fileName: string | undefined, isCharImage: boolean): string | undefined {
  if (!fileName) return undefined
  const base = resolveApiBase().replace(/\/$/, '')
  return `${base}/v1/screen/image?fileName=${encodeURIComponent(fileName)}&isCharImage=${isCharImage}`
}

// ---------- Email mailers ----------

export type ScreenEmailNoAddress = { name: string; email: string; phone: string; address: string }
export type ScreenEmailSyteline = ScreenEmailNoAddress & { state: string }
export type ScreenEmailHelp = { name: string; email: string; phone: string; state: string }

export async function postScreenEmailNoAddress(body: ScreenEmailNoAddress): Promise<{ success: boolean }> {
  return apiPost<{ success: boolean }>('/v1/screen/email/noAddress', body)
}

export async function postScreenEmailSyteline(body: ScreenEmailSyteline): Promise<{ success: boolean }> {
  return apiPost<{ success: boolean }>('/v1/screen/email/syteline', body)
}

export async function postScreenEmailHelp(body: ScreenEmailHelp): Promise<{ success: boolean }> {
  return apiPost<{ success: boolean }>('/v1/screen/email/help', body)
}
